import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL, INSTAGRAM_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import { ShieldCheck, Instagram, MapPin, Calendar, BookOpen, ExternalLink, Sparkles } from "lucide-react";

export default function Sobre() {
  usePageMeta(
    "Sobre a Viagens Orion",
    "Conheça a história da Viagens Orion: agência de turismo Cadastur fundada pela família Scabio. Sandra e Lucas contam a história por trás do Viagens Orion."
  );
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/about").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const sandra = data?.authors?.find((a) => a.slug === "sandra-scabio");
  const lucas = data?.authors?.find((a) => a.slug === "lucas-scabio");
  const team = data?.authors?.find((a) => a.slug === "equipe-viagens-orion");

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1" data-testid="sobre-page">
        {/* HERO */}
        <section className="relative bg-gradient-to-br from-[#2C1139] via-[#3d1b52] to-[#1c0a25] text-white overflow-hidden">
          <span className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />
          <span className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-orion-gold/20 blur-3xl" aria-hidden />

          <div className="container-orion py-16 md:py-28 relative grid md:grid-cols-[1fr,420px] gap-10 items-center">
            <div>
              <div className="cat-chip !text-orion-gold mb-3">Sobre a Orion</div>
              <h1 className="font-serif text-3xl md:text-6xl leading-[1.05]">
                Uma agência de família, feita por quem viaja de verdade.
              </h1>
              <p className="mt-5 text-base md:text-xl text-white/85 leading-relaxed max-w-2xl">
                De um sonho que nasceu na sala da família Scabio, em 2016, a uma agência consolidada, com selo Cadastur, mais de 30 destinos ativos e 150 roteiros exclusivos nacionais e internacionais. A Viagens Orion cresceu, mas nossa essência continua a mesma: planejar cada roteiro com a dedicação, o afeto e o cuidado que oferecemos à nossa própria família.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#1EBE57] transition-colors shadow-[0_10px_24px_rgba(37,211,102,0.35)]" data-testid="sobre-wa-btn">
                  <WhatsAppIcon size={14} color="#ffffff" /> Falar com a equipe
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs uppercase tracking-widest hover:border-orion-orange hover:text-orion-orange transition-colors">
                  <Instagram size={14} /> @viagensorion
                </a>
              </div>
              <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/60">
                <ShieldCheck size={12} className="text-orion-gold" /> Cadastur · CNPJ 37.476.039/0001-90 · SR SCABIO OPERADORA
              </div>
            </div>

            {team && (
              <div className="relative">
                <span className="absolute -inset-3 rounded-2xl bg-orion-orange/25 blur-2xl" aria-hidden />
                <span className="absolute -inset-[3px] rounded-2xl bg-gradient-to-br from-orion-gold via-orion-orange to-orion-gold opacity-80" aria-hidden />
                <img
                  src={team.photo_url}
                  alt="Equipe Viagens Orion"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width="600"
                  height="420"
                  className="relative w-full aspect-[4/3] object-cover rounded-2xl ring-4 ring-white/20 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                />
              </div>
            )}
          </div>
        </section>

        {/* STATS */}
        {data?.stats && (
          <section className="container-orion py-14 border-b border-[#EFEAE0]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              <div>
                <Sparkles size={22} className="mx-auto text-orion-orange mb-2" />
                <div className="font-serif text-3xl md:text-5xl text-orion-purple">8mil+</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A8191] mt-1">Sonhos Realizados</div>
              </div>
              <div>
                <BookOpen size={22} className="mx-auto text-orion-orange mb-2" />
                <div className="font-serif text-3xl md:text-5xl text-orion-purple">{data.stats.posts}</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A8191] mt-1">Artigos publicados</div>
              </div>
              <div>
                <MapPin size={22} className="mx-auto text-orion-orange mb-2" />
                <div className="font-serif text-3xl md:text-5xl text-orion-purple">150+</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A8191] mt-1">Roteiros exclusivos</div>
              </div>
              <div>
                <Calendar size={22} className="mx-auto text-orion-orange mb-2" />
                <div className="font-serif text-3xl md:text-5xl text-orion-purple">{new Date().getFullYear() - (data.story?.founded || 2016)}+</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A8191] mt-1">Anos de estrada</div>
              </div>
            </div>
          </section>
        )}

        {/* STORY */}
        <section className="container-orion py-16 max-w-3xl">
          <div className="cat-chip mb-3">Nossa história</div>
          <h2 className="font-serif text-2xl md:text-4xl text-orion-purple leading-tight">
            De uma família apaixonada por mapas para famílias que querem viajar sem preocupações.
          </h2>
          <div className="mt-6 space-y-4 text-[15px] md:text-[16px] text-[#3f2d4b] leading-relaxed">
            <p>
              <strong>Sandra Scabio</strong> transformou sua sólida formação em Hotelaria e Turismo e anos de vivência internacional na criação da Viagens Orion, em 2016. Ao seu lado, seu filho <strong>Lucas Scabio</strong> — Desenvolvedor de Sistemas e graduado em Marketing Digital — trouxe a inovação tecnológica para acelerar o crescimento e elevar a precisão de cada atendimento.
            </p>
            <p>
              Hoje, com mais de 30 destinos ativos e saídas mensais em grupo, a Orion combina a eficiência da tecnologia com o acolhimento humano para entregar viagens impecáveis e mágicas. Toda essa bagagem e paixão pela estrada você também encontra no <em>Viagens Orion</em>, o nosso espaço exclusivo de dicas de viagem.
            </p>
          </div>
        </section>

        {/* FOUNDERS */}
        {(sandra || lucas) && (
          <section className="bg-[#F7F1E8]/60 border-y border-[#EFEAE0]">
            <div className="container-orion py-14 md:py-20">
              <div className="cat-chip mb-3">Quem Escreve</div>
              <h2 className="font-serif text-2xl md:text-4xl text-orion-purple leading-tight mb-10">
                Quem Escreve
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {[sandra, lucas].filter(Boolean).map((a) => (
                  <div key={a.slug} className="bg-white border border-[#EFEAE0] p-6 flex flex-col md:flex-row gap-5">
                    <div className="relative shrink-0 self-center md:self-start">
                      <span className="absolute -inset-1 rounded-full bg-gradient-to-br from-orion-gold via-orion-orange to-orion-gold opacity-70" aria-hidden />
                      <img
                        src={a.photo_url}
                        alt={a.name}
                        loading="lazy"
                        decoding="async"
                        width="140"
                        height="140"
                        className="relative h-32 w-32 md:h-36 md:w-36 object-cover rounded-full ring-2 ring-white"
                        style={{ objectPosition: a.photo_position || "center" }}
                        data-testid={`sobre-photo-${a.slug}`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-orion-orange font-semibold">
                        {a.short_role}
                      </div>
                      <h3 className="font-serif text-2xl text-orion-purple mt-1">{a.name}</h3>
                      <p className="mt-2 text-sm text-[#594A63] leading-relaxed">{a.bio}</p>
                      <Link to={`/autor/${a.slug}`} className="mt-3 inline-flex items-center gap-1 text-orion-orange text-[11px] uppercase tracking-widest font-semibold hover:text-[#E04828]">
                        Ver posts assinados <ExternalLink size={11} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* GALLERY */}
        {data?.gallery && data.gallery.length > 0 && (
          <section className="container-orion py-16">
            <div className="cat-chip mb-3">Bastidores</div>
            <h2 className="font-serif text-2xl md:text-4xl text-orion-purple leading-tight mb-10">
              Um pouco do que a Orion já viveu com nossos viajantes
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {data.gallery.map((g) => (
                <figure key={g.slug} className="relative overflow-hidden rounded-xl bg-[#EFEAE0] aspect-[3/4] group">
                  <img src={g.url} alt={g.title} loading="lazy" decoding="async" width="600" height="800" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-orion-gold">{g.credit || "Viagens Orion"}</div>
                    <div className="font-serif text-sm md:text-lg leading-tight">{g.title}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* CADASTUR verification card */}
        <section className="container-orion pb-16">
          <div className="mx-auto max-w-3xl bg-orion-purple text-white p-6 md:p-8 rounded-2xl relative overflow-hidden">
            <span className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
              <div className="bg-white rounded-2xl p-4 shrink-0 self-center md:self-auto">
                <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/o3ocgnzf_cadastur-logo.webp" alt="Cadastur" width="140" height="60" className="h-12 w-auto max-w-[150px] object-contain" />
              </div>
              <div className="text-center md:text-left flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.32em] text-orion-gold font-semibold flex items-center justify-center md:justify-start gap-2">
                  <ShieldCheck size={13} /> Agência oficial verificada
                </div>
                <h3 className="font-serif text-lg md:text-xl mt-2">Confira a agência no site oficial do Ministério do Turismo</h3>
                <p className="text-xs md:text-sm text-white/70 mt-2">SR SCABIO OPERADORA · CNPJ 37.476.039/0001-90</p>
              </div>
              <a href={data?.story?.cadastur_url || "https://cadastur.turismo.gov.br/hotsite/#!/public/sou-turista/inicio"} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center justify-center gap-2 bg-orion-orange px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E04828] transition-colors whitespace-nowrap">
                Consultar no Gov <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <div className="container-orion pb-16 max-w-3xl mx-auto">
          <NewsletterSignup variant="editorial" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
